import { useCallback, useMemo, useState } from "react";

import { App_Url_New, EMAIL_TEMPLATE_KEYS, LOB_IDS } from "@/shared/common/static";
import { axiosInstance } from "@/shared/lib/axiosInstance";
import { showToast } from "@/shared/lib/toast";

import type { useQuoteRefs } from "./useQuoteRefs";

type Refs = ReturnType<typeof useQuoteRefs>;

type SessionContact = {
  email: string;
  customerName: string;
};

type Options = {
  lobId: number;
  refs: Refs;
  selectedQuote: any;
  clientId: number | string | null;
  sessionId: number | string | null;
  isPostDataReady: boolean;
  sessionContact?: SessionContact;
};

const getUploadPathByLob = (lobId: number) => {
  if (lobId === LOB_IDS.MOTOR) return App_Url_New.D2CPageUrl.insurance.carGetQuote;
  if (lobId === LOB_IDS.HEALTH) return App_Url_New.D2CPageUrl.insurance.healthGetQuote;
  if (lobId === LOB_IDS.LIFE) return App_Url_New.D2CPageUrl.insurance.lifeGetQuote;
  return App_Url_New.D2CPageUrl.insurance.travelGetQuote;
};

const getFrontBaseUrl = () =>
  import.meta.env.VITE_APP_INSUREHUB_FRONTEND_URL ||
  import.meta.env.VITE_APP_FRONTEND_URL ||
  "https://protego2.nexsysit.co.in";

export const useClientLinks = ({
  lobId,
  refs,
  selectedQuote,
  clientId,
  sessionId,
  isPostDataReady,
  sessionContact,
}: Options) => {
  const [isSendingUploadLink, setIsSendingUploadLink] = useState(false);
  const [isSendingPaymentLink, setIsSendingPaymentLink] = useState(false);

  const clientContact = useMemo(() => {
    let fromRefs: SessionContact = { email: "", customerName: "" };

    if (lobId === LOB_IDS.MOTOR) {
      const values = refs.carPersonalRef.current?.getValues() as any;
      fromRefs = {
        email: values?.email || "",
        customerName: `${values?.firstName || ""} ${values?.lastName || ""}`.trim(),
      };
    } else if (lobId === LOB_IDS.TRAVEL) {
      const values = refs.travelPersonalRef.current?.getValues() as any;
      fromRefs = {
        email: values?.email || "",
        customerName: `${values?.firstName || ""} ${values?.lastName || ""}`.trim(),
      };
    } else if (lobId === LOB_IDS.LIFE) {
      const values = (refs as any).lifePersonalRef?.current?.getValues() as any;
      fromRefs = {
        email: values?.email || "",
        customerName: `${values?.firstName || ""} ${values?.lastName || ""}`.trim(),
      };
    } else {
      const values = refs.healthPersonalRef.current?.getValues() as any;
      fromRefs = {
        email: values?.email || "",
        customerName: `${values?.firstName || ""} ${values?.lastName || ""}`.trim(),
      };
    }

    return {
      email: fromRefs.email || sessionContact?.email || "",
      customerName: fromRefs.customerName || sessionContact?.customerName || "Customer",
    };
  }, [lobId, refs, sessionContact]);

  const quoteId =
    selectedQuote?.quote_id ?? selectedQuote?.id ?? selectedQuote?.quoteId ?? sessionId ?? null;

  const handleSendUploadLinkToClient = useCallback(async () => {
    if (!clientContact.email) {
      showToast("Cannot send link: Customer email is missing.", "error");
      return;
    }

    if (!clientId || !sessionId || !quoteId) {
      showToast("Cannot send link: Missing client/session/quote details.", "error");
      return;
    }

    try {
      setIsSendingUploadLink(true);

      const response = await axiosInstance.post(App_Url_New.ApiEndPoint.sendUploadDocLink, {
        template_key: EMAIL_TEMPLATE_KEYS.UPLOAD_DOC_LINK,
        email: clientContact.email,
        channels: [
          {
            type: "email",
            provider: "azure",
            enabled: true,
          },
        ],
        payload: {
          customer_name: clientContact.customerName,
          client_id: Number(clientId),
          quote_id: Number(quoteId),
          session_id: Number(sessionId),
          lob_id: lobId,
          step: "4",
          upload_link: `${getFrontBaseUrl()}${getUploadPathByLob(lobId)}`,
        },
      });

      const responseStatus = response?.data?.status;
      const isSuccess =
        response?.status === 200 || responseStatus === "success" || responseStatus === true;
      if (!isSuccess) {
        throw new Error(response?.data?.message || "Upload link request failed");
      }

      showToast("Upload link sent successfully!", "success");
    } catch (error) {
      console.error("Failed to send upload link", error);
      showToast("Failed to send upload link.", "error");
    } finally {
      setIsSendingUploadLink(false);
    }
  }, [clientContact, clientId, lobId, quoteId, sessionId]);

  const handleSendPaymentLinkToClient = useCallback(async () => {
    if (!isPostDataReady) {
      showToast(
        "Complete and save upload documents + additional details before sending payment link.",
        "error"
      );
      return;
    }

    if (!clientContact.email) {
      showToast("Cannot send payment link: Customer email is missing.", "error");
      return;
    }

    if (!clientId || !sessionId || !quoteId) {
      showToast("Cannot send payment link: Missing client/session/quote details.", "error");
      return;
    }

    try {
      setIsSendingPaymentLink(true);

      const response = await axiosInstance.post(App_Url_New.ApiEndPoint.sendPaymentLink, {
        template_key: EMAIL_TEMPLATE_KEYS.PAYMENT_LINK,
        email: clientContact.email,
        channels: [
          {
            type: "email",
            provider: "azure",
            enabled: true,
          },
        ],
        payload: {
          customer_name: clientContact.customerName,
          client_id: Number(clientId),
          quote_id: Number(quoteId),
          session_id: Number(sessionId),
          lob_id: lobId,
          step: "7",
          payment_link: `${getFrontBaseUrl()}${getUploadPathByLob(lobId)}`,
        },
      });

      const responseStatus = response?.data?.status;
      const isSuccess =
        response?.status === 200 || responseStatus === "success" || responseStatus === true;
      if (!isSuccess) {
        throw new Error(response?.data?.message || "Payment link request failed");
      }

      showToast("Payment link sent successfully!", "success");
    } catch (error) {
      console.error("Failed to send payment link", error);
      showToast("Failed to send payment link.", "error");
    } finally {
      setIsSendingPaymentLink(false);
    }
  }, [clientContact, clientId, isPostDataReady, lobId, quoteId, sessionId]);

  return {
    isSendingUploadLink,
    isSendingPaymentLink,
    handleSendUploadLinkToClient,
    handleSendPaymentLinkToClient,
  };
};
